// Force a return to the login screen on AUTHENTICATION failures, app-wide.
//
// This is deliberately narrow. An expired or invalid session token is the
// common cause of confusing "data not available" states, and the only fix is
// to sign in again -- so we bounce to login. But a source system being down,
// or a 403 forbidden, is NOT an auth problem: the user is validly logged in,
// and logging them out neither helps nor is honest about what went wrong.
// Those keep their in-place messages.
//
// Installed once by wrapping window.fetch. EventSource (chat SSE) does not go
// through fetch, so streaming is untouched.

const LOGIN_PATH = (process.env.PUBLIC_URL || "") + "/login";

let redirecting = false;

function forceLogin() {
  if (redirecting) return;                 // a burst of 401s must redirect once
  try {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
  } catch { /* ignore */ }
  // Already on login? Do nothing, so a bad-password attempt is not reloaded.
  if (!window.location.pathname.endsWith("/login")) {
    redirecting = true;
    // replace, not assign: the expired page should not sit in history for the
    // back button to return to.
    window.location.replace(LOGIN_PATH);
  }
}

function urlOf(input) {
  return typeof input === "string" ? input : (input && input.url) || "";
}

async function isAuthFailure(res, url) {
  if (!url.includes("/api/")) return false;
  // Login owns its own 401 (wrong password) so its message survives.
  if (url.includes("/api/login")) return false;

  // 401: expired or missing token -- unambiguously an auth failure.
  if (res.status === 401) return true;

  // 422: flask-jwt-extended uses this for a malformed/!string token. But 422
  // is also a normal form-validation status, so only treat it as auth when
  // the body actually names a token problem. Peek via a clone to leave the
  // caller's response intact.
  if (res.status === 422) {
    try {
      const body = await res.clone().json();
      const msg = String(body?.msg || body?.error || "").toLowerCase();
      // flask-jwt-extended / PyJWT 422 messages for a bad token take several
      // forms: "Not enough segments", "Signature verification failed",
      // "Subject must be a string", "Invalid header string: ...", "Invalid
      // crypto padding", "Invalid payload string". Match the auth-specific
      // fragments; deliberately avoid broad words that a normal form-
      // validation 422 might contain.
      return /token|segment|signature|subject|authorization|header string|crypto padding|payload string/.test(msg);
    } catch {
      return false;
    }
  }
  return false;
}

let installed = false;

export function installAuthGuard() {
  if (installed || typeof window === "undefined" || !window.fetch) return;
  installed = true;
  const orig = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const res = await orig(input, init);
    try {
      if (await isAuthFailure(res, urlOf(input))) forceLogin();
    } catch { /* never let the guard break a request */ }
    return res;
  };
}
